import React, {FC, useCallback, useContext, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import Box from '@mui/material/Box';

import {ORGANIZATION_TEMPLATES_LIST, ORGANIZATION_TEMPLATES_DEFAULT_LIST} from 'appRedux/actions/organization';
import {GET_ORGANIZATION_LANGUAGES_LIST} from 'appRedux/actions/organizationLanguage';
import {RootReducer} from 'appRedux/reducers';

import {AdminContext} from 'contexts/admin/context';
import {AlertContext} from 'contexts/alert/context';

import ClientSectionWrapper from 'components/ClientScreenComponents/SectionWrapper';
import DataGridWrapper from 'components/AdminScreenComponents/DataGridWrapper';

import {
    OrganizationTemplateTableItemType,
    getOrganizationTemplateTableItems,
} from 'pages/admin/organizationTemplates/helper';
import TranslationSelector from 'pages/admin/organizationTemplates/TranslationSelector';
import OrganizationTemplatesDataGrid from 'pages/admin/organizationTemplates/OrganizationTemplatesDataGrid';

import {getLanguageById, getLanguageIdByName, getDefaultLanguage} from 'helpers/translationsHelper';

import {PARAMETER_LANGUAGE} from 'config/index';

const OrganizationTemplatesList: FC = () => {
    const [t] = useTranslation();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    const {myOrganization} = useContext(AdminContext);
    const {showAlert} = useContext(AlertContext);

    const getOrganizationTemplates = useCallback(
        data => dispatch({type: ORGANIZATION_TEMPLATES_LIST.REQUEST, payload: data}),
        [dispatch],
    );

    const getOrganizationTemplatesDefaultList = useCallback(
        data => dispatch({type: ORGANIZATION_TEMPLATES_DEFAULT_LIST.REQUEST, payload: data}),
        [dispatch],
    );

    const getOrganizationLanguages = useCallback(
        data => dispatch({type: GET_ORGANIZATION_LANGUAGES_LIST.REQUEST, payload: data}),
        [dispatch],
    );

    const {
        admin: {isLoadingOrganizationTemplatesList, languageList, organizationTemplates},
    } = useSelector<RootReducer>((state: RootReducer) => state) as RootReducer;

    const currentOrganizationDefaultLanguage = myOrganization
        ? getLanguageById(languageList, myOrganization.language)
        : getDefaultLanguage();

    const currentLanguage = searchParams.get(PARAMETER_LANGUAGE)
        ? searchParams.get(PARAMETER_LANGUAGE)
        : currentOrganizationDefaultLanguage.name;

    useEffect(() => {
        if (myOrganization && currentLanguage) {
            getOrganizationLanguages({
                id: myOrganization.id,
                showAlert,
            });
        }
    }, []);

    useEffect(() => {
        if (myOrganization && currentLanguage) {
            getOrganizationTemplates({
                id: myOrganization.id,
                lang: currentLanguage,
                showAlert,
            });
            getOrganizationTemplatesDefaultList({
                id: myOrganization.id,
                lang: currentLanguage,
                showAlert,
            });
        }
    }, [myOrganization, currentLanguage]);

    if (!myOrganization || !currentLanguage) return null;

    const organizationTemplateTableItems: OrganizationTemplateTableItemType[] = getOrganizationTemplateTableItems(
        organizationTemplates,
        getLanguageIdByName(languageList, currentLanguage, currentOrganizationDefaultLanguage.id),
    );

    return (
        <ClientSectionWrapper>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <TranslationSelector organizationLanguage={currentOrganizationDefaultLanguage} />
            </Box>
            <DataGridWrapper
                itemsList={organizationTemplateTableItems}
                itemsTotalNumber={organizationTemplateTableItems.length}
            >
                {!isLoadingOrganizationTemplatesList && organizationTemplateTableItems && (
                    <OrganizationTemplatesDataGrid
                        items={organizationTemplateTableItems}
                        currentLanguage={currentLanguage}
                    />
                )}
            </DataGridWrapper>
        </ClientSectionWrapper>
    );
};

export default OrganizationTemplatesList;
